import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { InteractionManager } from 'react-native';

import { Alert } from '@components/Alert';
import { LocalFile } from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { NEW_BOTTOM_SHEET_MAX_DOC_COUNT } from '@schemas/chatInputSchema';

const INITIAL_SYNC_DELAY = 500;

interface MenusContextValue {
  onSelectFiles: (files: LocalFile[]) => void;
  localFiles: LocalFile[];
  setLocalFiles: (files: LocalFile[]) => void;
}

const initialMenusContext: MenusContextValue = {
  onSelectFiles: () => void 0,
  localFiles: [],
  setLocalFiles: () => void 0,
};

const MenusContext = createContext<MenusContextValue>(initialMenusContext);

interface MenusProviderProps {
  children: ReactNode;
}

export const MenusProvider: React.FC<MenusProviderProps> = ({ children }) => {
  const { myChatLocalFiles } = useChatInput();
  const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
  const { t } = useTranslation();
  const { directUploadFiles } = useChatUploadManager();

  const handleUploadAndUpdate = useCallback(
    async (toUpload: LocalFile[]) => {
      try {
        const uploaded = await directUploadFiles(toUpload);

        setLocalFiles((prev) => {
          const fileMap = new Map(prev.map((file) => [file.clientId, file]));

          uploaded.forEach((item) => {
            const existing = fileMap.get(item.clientId);
            if (existing) {
              fileMap.set(item.clientId, {
                ...existing,
                blobId: item.blobId,
              });
            }
          });

          return Array.from(fileMap.values());
        });
      } catch (error) {
        console.error('Error uploading files:', error);
      }
    },
    [directUploadFiles]
  );

  const normalizeFile = useCallback((file: LocalFile): LocalFile => {
    return {
      ...file,
      duration: file.duration ?? null,
      size: file.size ?? null,
      blobId: file.blobId ?? '',
    };
  }, []);

  const onSelectFiles = useCallback(
    (filesToUpload: LocalFile[]) => {
      const totalCount = localFiles.length + filesToUpload.length;

      if (totalCount > NEW_BOTTOM_SHEET_MAX_DOC_COUNT) {
        Alert.alert(t('models:chat.input.maxSelection'));
        return;
      }

      setLocalFiles((prev) => {
        const fileMap = new Map(prev.map((f) => [f.clientId, f]));
        filesToUpload.forEach((file) => {
          fileMap.set(file.clientId, normalizeFile(file));
        });

        return Array.from(fileMap.values());
      });

      const filesToUploadList = filesToUpload.filter(
        (f) => !f.blobId || f.blobId === ''
      );

      if (filesToUploadList.length > 0) {
        InteractionManager.runAfterInteractions(() => {
          handleUploadAndUpdate(filesToUploadList);
        });
      }
    },
    [localFiles.length, normalizeFile, handleUploadAndUpdate, t]
  );

  useEffect(() => {
    if (myChatLocalFiles && myChatLocalFiles.length > 0) {
      const timer = setTimeout(() => {
        // Only sync files that aren't already in localFiles (to prevent re-adding deleted files)
        const existingClientIds = new Set(localFiles.map((f) => f.clientId));
        const filesToSync = myChatLocalFiles.filter(
          (f) => !existingClientIds.has(f.clientId)
        );

        if (filesToSync.length > 0) {
          onSelectFiles(filesToSync);
        }
      }, INITIAL_SYNC_DELAY);

      return () => clearTimeout(timer);
    }
  }, [myChatLocalFiles, onSelectFiles, localFiles]);

  const contextValue = useMemo(
    () => ({
      onSelectFiles,
      localFiles,
      setLocalFiles,
    }),
    [onSelectFiles, localFiles]
  );

  return (
    <MenusContext.Provider value={contextValue}>
      {children}
    </MenusContext.Provider>
  );
};

export const useMenus = (): MenusContextValue => {
  const context = useContext(MenusContext);

  if (!context) {
    throw new Error('useMenus must be used within a MenusProvider');
  }

  return context;
};
